import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api_editPartyAccessory } from '../../../api/catering/partyAccessory';
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';

type SubType = {
	name: string;
	description: string;
};

const EditPartyAccessory = () => {
	const navigate = useNavigate();
	const partyAccessory = useLocation().state;
	const [loading, setLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>(partyAccessory?.name || '');
	const [description, setDescription] = useState<string>(partyAccessory?.description || '');
	const [fromPrice, setFromPrice] = useState<number | string>(partyAccessory?.price?.from || '');
	const [toPrice, setToPrice] = useState<number | string>(partyAccessory?.price?.to || '');
	const [subCategories, setSubCategories] = useState<SubType[]>(
		partyAccessory?.subPartyAccessories || [],
	);
	const [customSubTypes, setCustomSubTypes] = useState<SubType[]>([]);
	const [customSubSubTypes, setCustomSubSubTypes] = useState<SubType[]>([]);
	const [customSubSubSubTypes, setCustomSubSubSubTypes] = useState<SubType[]>([]);

	useEffect(() => {
		// Update subCategories state if it exists in partyAccessory
		if (partyAccessory.subPartyAccessories) {
			setSubCategories([...partyAccessory.subPartyAccessories]); // Copy array to state
		} else {
			setSubCategories([]); // Clear subCategories state if it doesn't exist
		}
	}, [partyAccessory.subPartyAccessories]);
	const handleCustomSubTypeChange = (index: number, field: keyof SubType, value: string) => {
		const newCustomSubTypes = [...customSubTypes];
		newCustomSubTypes[index][field] = value;
		setCustomSubTypes(newCustomSubTypes);
	};

	const handleCustomSubSubTypeChange = (index: number, field: keyof SubType, value: string) => {
		const newCustomSubSubTypes = [...customSubSubTypes];
		newCustomSubSubTypes[index][field] = value;
		setCustomSubSubTypes(newCustomSubSubTypes);
	};

	const handleSubTypeChange = (index: number, field: keyof SubType, value: string | number) => {
		const updatedSubCategories = [...subCategories];
		updatedSubCategories[index] = {
			...updatedSubCategories[index],
			[field]: value,
		};
		setSubCategories(updatedSubCategories);
	};

	const handleSubAccessories = () => {
		setCustomSubTypes([...customSubTypes, { name: '', description: '' }]);
	};
	const handleSubSubAccessories = () => {
		setCustomSubSubTypes([...customSubSubTypes, { name: '', description: '' }]);
	};
	const handleSubSubSubAccessories = () => {
		setCustomSubSubSubTypes([...customSubSubSubTypes, { name: '', description: '' }]);
	};

	const editPartyAccessory = async () => {
		// Validate required fields
		if (!name) {
			myToastWarning('Please Enter Name');
			return;
		}

		try {
			setLoading(true);

			// Prepare payload
			const payload = {
				name,
				description,
				price: {
					from: Number(fromPrice),
					to: Number(toPrice),
				},
				subPartyAccessories: subCategories.map((subType) => ({
					name: subType.name,
					description: subType.description,
				})),
			};

			// Check if custom subtypes need to be added
			if (subCategories.length === 0) {
				customSubTypes.forEach((subAccessories) => {
					const subAccessoryObj: {
						name: string;
						description: string;
						subsubPartyAccessories?: {
							name: string;
							description: string;
						}[];
					} = {
						name: subAccessories.name,
						description: subAccessories.description,
						subsubPartyAccessories: [],
					};

					// Append subSubAccessories if present
					if (customSubSubTypes) {
						customSubSubTypes.forEach((subSubAccessories) => {
							const subSubAccessoryObj: {
								name: string;
								description: string;
							} = {
								name: subSubAccessories.name,
								description: subSubAccessories.description,
							};

							// Push to subSubPartyAccessories if defined
							if (subAccessoryObj.subsubPartyAccessories !== undefined) {
								subAccessoryObj.subsubPartyAccessories.push(subSubAccessoryObj);
							}
						});
					}

					payload.subPartyAccessories.push(subAccessoryObj);
				});
			}

			// Edit existing party accessory
			const res = await api_editPartyAccessory(payload, partyAccessory._id);

			// Handle response
			const message = typeof res.message === 'string' ? res.message : 'Success';

			if (res.isSuccess) {
				toast.success(message || 'Accessory updated successfully');
				setTimeout(() => {
					navigate(-1); // Navigate back to previous page
				}, 1000);
			} else {
				toast.error(message || 'Failed to update accessory');
			}
		} catch (error) {
			console.error('Error editing accessory:', error);
			toast.error('Failed to update accessory');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				flex: 1,
				padding: '40px',
			}}>
			<div className='mb-3'>
				<h3>Edit Party Accessory</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='name' className='form-label'>
					Name
				</label>
				<input
					id='name'
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter name'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='description' className='form-label'>
					Description
				</label>
				<input
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter description'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='fromPrice' className='form-label'>
					From Price
				</label>
				<input
					id='fromPrice'
					type='number'
					value={fromPrice}
					onChange={(e) => setFromPrice(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter from price'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='toPrice' className='form-label'>
					To Price
				</label>
				<input
					id='toPrice'
					type='number'
					value={toPrice}
					onChange={(e) => setToPrice(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter to price'
				/>
			</div>

			<div className='mb-3'>
				<button onClick={handleSubAccessories} className='btn btn-secondary'>
					Add Sub Accessories
				</button>
			</div>

			{subCategories.length > 0 && (
				<div className='mb-3'>
					<label className='form-label'>Edit Sub Party Accessory:</label>
					{subCategories.map((subType, index) => (
						<div key={index} className='mb-3'>
							<label htmlFor={`subTypeName-${index}`} className='form-label'>
								Name
							</label>
							<input
								id={`subTypeName-${index}`}
								value={subType.name}
								onChange={(e) => handleSubTypeChange(index, 'name', e.target.value)}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter name'
							/>

							<label htmlFor={`subTypeDesc-${index}`} className='form-label'>
								Description
							</label>
							<input
								id={`subTypeDesc-${index}`}
								value={subType.description}
								onChange={(e) =>
									handleSubTypeChange(index, 'description', e.target.value)
								}
								style={{ width: '400px' }}
								className='form-control'
								placeholder='Enter description'
							/>
						</div>
					))}
				</div>
			)}

			{customSubTypes.map((subType, index) => (
				<div key={index} className='mb-3' style={{ width: '400px' }}>
					<input
						value={subType.name}
						onChange={(e) => handleCustomSubTypeChange(index, 'name', e.target.value)}
						className='form-control'
						placeholder='Enter sub type name'
					/>
					<input
						value={subType.description}
						onChange={(e) =>
							handleCustomSubTypeChange(index, 'description', e.target.value)
						}
						className='form-control'
						placeholder='Enter sub type description'
						style={{ marginTop: '10px' }}
					/>
					<div className='mb-3'>
						<button onClick={handleSubSubAccessories} className='btn btn-secondary'>
							Add Sub Sub Accessories
						</button>
					</div>
				</div>
			))}

			<button
				onClick={editPartyAccessory}
				type='button'
				className='btn btn-lg btn-primary'
				disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default EditPartyAccessory;
